// Libs
import React, { useState, useContext, useEffect } from "react";
import { useObserver, KeyValuePair, FieldType, Else, TypographyX } from "@shoothill/core";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { FormLabel, Box, Typography, Checkbox, FormControlLabel, Radio, FormControl, FormHelperText, Select, InputAdornment, TextField, useMediaQuery } from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import styled from "styled-components";

// App
import { StoresContext, Stores } from "Globals/Stores";

// Models
import { HomeOwnerStatusEnum } from "Globals/Models/HomeOwnerStatusEnum";
import { PropertyDetailsModel } from "Globals/Models/Domain/FormSections/PropertyDetails/PropertyDetailsModel";

// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";
import PropertyDetailsViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PropertyDetailsViewModel";

// Views
import { CurrencyInput } from "Globals/Views/CurrencyInput";
import { CustomYesNoRadio } from "Globals/Views/CustomYesNoRadio";

// Styles
import { theme } from "Globals/Styles/AppTheme";
import { StyledButton } from "Globals/Styles/ButtonStyles";
import { CustomTwoSelects } from "Globals/Styles/CustomTwoSelects";
import { Row } from "Globals/Styles/LayoutStyles";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";
import { CustomInput } from "Globals/Styles/AppStyling";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";

interface IProps {
    applicant: ApplicantViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}

export const NewPropertyDetailsView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [viewModel] = useState(props.applicant.newPropertyDetailsViewModel);
    const isMobile: boolean = useMediaQuery("screen and (max-width: 767px)") ? true : false;

    const isPrimaryApplicant = (): boolean => {
        let applicants = props.applicationFormViewModel.applicantViewModels;
        let firstApplicant = applicants.find((a) => a.model.primaryApplicant);
        if (firstApplicant) {
            if (props.applicant.model.id == firstApplicant.model.id) return true;
        }
        return false;
    };

    useEffect(() => {
        viewModel.handleCheckboxChange("sameForApplicantTwo", true);
        if (props.applicationFormViewModel.applicantViewModels.find((a) => a.model.primaryApplicant === false)) {
            props.applicationFormViewModel.applicantViewModels.find((a) => a.model.primaryApplicant === false)!.updateSectionCompleted(FormSectionsEnum.NewPropertyDetails, true);
        }
    }, []);

    // JJ: This is the logic we want, but it throws React Hook issues.
    // Using CSS Styling as a work around.
    // if (isPrimaryApplicant() === false) {
    //     return <></>;
    // }

    return useObserver(() => (
        <div
            onKeyUp={() => viewModel.restartTimer()}
            style={{
                display: isPrimaryApplicant() === false ? "none" : "block",
            }}
        >
            {viewModel.DevTool_ShowCanProceed && <h5 style={{ color: "red" }}>Can Proceed: {viewModel.canProceed ? "Yes" : "No"}</h5>}
            <Row>
                <Typography variant={"h6"}>If you are provisionally offered a property we will request from you supporting documentation</Typography>
            </Row>
            <Row>
                <CustomInput
                    label="Development name"
                    placeHolder="Enter the development name"
                    viewModel={viewModel}
                    fieldName="developmentName"
                    shrink={true}
                    onChange={() => viewModel.handleInputChange()}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row style={isMobile ? {} : { alignItems: "flex-end" }}>
                <FormControl component="fieldset">
                    <FormLabel style={{ marginBottom: "10px", fontSize: "12px" }} component="legend">
                        Property size and type
                    </FormLabel>
                    <CustomInput
                        type="select"
                        style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                        placeHolder={"Please select"}
                        label={<span style={{ fontSize: "10px", fontWeight: "normal" }}>(e.g. 2 bedroom house)</span>}
                        viewModel={viewModel}
                        fieldName="propertySizeId"
                        selectItems={storesContext.Domain.PropertySizeStore.getPropertySizeOptions}
                        inputProps={{
                            disabled: props.applicationFormViewModel.model.isSubmitted,
                        }}
                    />
                </FormControl>
                {/* SW removed for 11548 */}
                {/* <CustomInput
                    type="select"
                    style={isMobile ? { width: "100%" } : { minWidth: "350px" }}
                    placeHolder={"Please select"}
                    viewModel={viewModel}
                    fieldName="propertyTypeId"
                    selectItems={storesContext.Domain.PropertyTypeStore.getPropertyTypeOptions}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                /> */}
            </Row>
            <Row>
                <CustomInput
                    label="Address line 1"
                    placeHolder="Address line 1"
                    viewModel={viewModel}
                    fieldName="addressLine1"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Address line 2"
                    placeHolder="Address line 2"
                    viewModel={viewModel}
                    fieldName="addressLine2"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Address line 3"
                    placeHolder="Address line 3"
                    viewModel={viewModel}
                    fieldName="addressLine3"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomInput
                    label="2nd choice address line 1"
                    placeHolder="2nd choice address line 1"
                    viewModel={viewModel}
                    fieldName="secondChoiceAddressLine1"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="2nd choice address line 2"
                    placeHolder="2nd choice address line 2"
                    viewModel={viewModel}
                    fieldName="secondChoiceAddressLine2"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="2nd choice address line 3"
                    placeHolder="2nd choice address line 3"
                    viewModel={viewModel}
                    fieldName="secondChoiceAddressLine3"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            <Row>
                <CustomInput
                    label="3rd choice address line 1"
                    placeHolder="3rd choice address line 1"
                    viewModel={viewModel}
                    fieldName="thirdChoiceAddressLine1"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="3rd choice address line 2"
                    placeHolder="3rd choice address line 2"
                    viewModel={viewModel}
                    fieldName="thirdChoiceAddressLine2"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="3rd choice address line 3"
                    placeHolder="3rd choice address line 3"
                    viewModel={viewModel}
                    fieldName="thirdChoiceAddressLine3"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row>
            {/* SW removed for 11548 */}
            {/* <Row>
                <CustomInput
                    label="City/Town"
                    placeHolder="City/Town"
                    viewModel={viewModel}
                    fieldName="city"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Property county"
                    placeHolder="County"
                    viewModel={viewModel}
                    fieldName="county"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
                <CustomInput
                    label="Postcode"
                    placeHolder="Postcode"
                    viewModel={viewModel}
                    fieldName="postcode"
                    shrink={true}
                    onChange={() => {
                        viewModel.handleInputChange();
                    }}
                    inputProps={{
                        disabled: props.applicationFormViewModel.model.isSubmitted,
                    }}
                />
            </Row> */}
        </div>
    ));
};
